import {
  CLINIC_CRF_V2,
  type ClinicCrfCondition,
  type ClinicCrfData,
  type ClinicCrfDefinitionV2,
  type ClinicCrfField,
  type DataValidationResult,
  type FieldEvaluation,
  type MissingValue,
  type PartialDateValue,
  type RuleEvaluation,
} from './types'

const FORBIDDEN_PATH_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor'])

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

/** JSON Schema and PHP mb_strlen both count Unicode code points, not UTF-16 units. */
export function unicodeLength(value: string): number {
  return Array.from(value).length
}

export function pathSegments(path: string): string[] {
  const segments = path.split('.')
  if (segments.some((segment) => FORBIDDEN_PATH_SEGMENTS.has(segment))) {
    throw new Error('Unsafe clinic-crf path.')
  }
  return segments
}

export function hasAtPath(data: ClinicCrfData, path: string): boolean {
  let cursor: unknown = data
  for (const segment of pathSegments(path)) {
    if (!isRecord(cursor) || !Object.prototype.hasOwnProperty.call(cursor, segment)) return false
    cursor = cursor[segment]
  }
  return true
}

export function getAtPath(data: ClinicCrfData, path: string): unknown {
  let cursor: unknown = data
  for (const segment of pathSegments(path)) {
    if (!isRecord(cursor) || !Object.prototype.hasOwnProperty.call(cursor, segment)) return undefined
    cursor = cursor[segment]
  }
  return cursor
}

export function setAtPath(data: ClinicCrfData, path: string, value: unknown): void {
  const segments = pathSegments(path)
  let cursor = data
  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      cursor[segment] = value
      return
    }
    const next = cursor[segment]
    if (!isRecord(next)) cursor[segment] = {}
    cursor = cursor[segment] as ClinicCrfData
  })
}

export function deleteAtPath(data: ClinicCrfData, path: string): void {
  const segments = pathSegments(path)
  const stack: Array<[ClinicCrfData, string]> = []
  let cursor = data
  for (const segment of segments.slice(0, -1)) {
    const next = cursor[segment]
    if (!isRecord(next)) return
    stack.push([cursor, segment])
    cursor = next
  }
  delete cursor[segments.at(-1) as string]
  for (const [parent, segment] of stack.reverse()) {
    const child = parent[segment]
    if (isRecord(child) && Object.keys(child).length === 0) delete parent[segment]
  }
}

export function isMissingValue(value: unknown): value is MissingValue {
  return isRecord(value) && isRecord(value.__missing)
}

export function isBlank(value: unknown, present = true): boolean {
  return !present || value === null || value === '' || (Array.isArray(value) && value.length === 0)
}

function comparableValue(value: unknown): unknown {
  if (isRecord(value) && Number.isInteger(value.year)) {
    let result = String(value.year).padStart(4, '0')
    if (Number.isInteger(value.month)) result += `-${String(value.month).padStart(2, '0')}`
    if (Number.isInteger(value.day)) result += `-${String(value.day).padStart(2, '0')}`
    return result
  }
  return value
}

function orderedCompare(left: unknown, right: unknown, accept: (comparison: number) => boolean): boolean {
  if (typeof left === 'number' && Number.isFinite(left) && typeof right === 'number' && Number.isFinite(right)) {
    return accept(left === right ? 0 : left < right ? -1 : 1)
  }
  if (typeof left === 'string' && typeof right === 'string') {
    return accept(left === right ? 0 : left < right ? -1 : 1)
  }
  return false
}

export function evaluateCondition(condition: ClinicCrfCondition, data: ClinicCrfData): boolean {
  if ('all' in condition) return condition.all.every((child) => evaluateCondition(child, data))
  if ('any' in condition) return condition.any.some((child) => evaluateCondition(child, data))
  if ('not' in condition) return !evaluateCondition(condition.not, data)

  const present = hasAtPath(data, condition.field)
  const leftRaw = present ? getAtPath(data, condition.field) : null
  const leftMissing = isMissingValue(leftRaw)
  if (condition.operator === 'present') {
    return present && !leftMissing && leftRaw !== null && leftRaw !== '' && !(Array.isArray(leftRaw) && leftRaw.length === 0)
  }
  if (condition.operator === 'missing') return leftMissing
  if (!present || leftMissing) return false

  const rightRaw = condition.compare_field
    ? getAtPath(data, condition.compare_field)
    : condition.value
  if (isMissingValue(rightRaw)) return false

  const left = comparableValue(leftRaw)
  const right = comparableValue(rightRaw)
  switch (condition.operator) {
    case 'eq':
      return left === right
    case 'neq':
      return left !== right
    case 'gt':
      return orderedCompare(left, right, (result) => result > 0)
    case 'gte':
      return orderedCompare(left, right, (result) => result >= 0)
    case 'lt':
      return orderedCompare(left, right, (result) => result < 0)
    case 'lte':
      return orderedCompare(left, right, (result) => result <= 0)
    case 'in':
      return Array.isArray(right) && right.some((candidate) => candidate === left)
    case 'not_in':
      return Array.isArray(right) && !right.some((candidate) => candidate === left)
    default:
      return false
  }
}

export function fieldIsVisible(field: ClinicCrfField, data: ClinicCrfData): boolean {
  return !field.visible_when || evaluateCondition(field.visible_when, data)
}

export function fieldIsRequired(field: ClinicCrfField, data: ClinicCrfData): boolean {
  return Boolean(field.required) || Boolean(field.required_when && evaluateCondition(field.required_when, data))
}

function validDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return false
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

function validDateTime(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/.exec(value)
  if (!match) return false

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  const second = Number(match[6])
  if (!validDate(`${match[1]}-${match[2]}-${match[3]}`) || hour > 23 || minute > 59 || second > 59) return false

  const offset = match[8]!
  if (offset !== 'Z') {
    const offsetHour = Number(offset.slice(1, 3))
    const offsetMinute = Number(offset.slice(4, 6))
    if (offsetHour > 23 || offsetMinute > 59) return false
  }

  // Date.parse is retained as a final platform sanity check, while the explicit
  // calendar checks above prevent JavaScript from silently rolling 30 February.
  return Number.isFinite(year) && !Number.isNaN(Date.parse(value))
}

function validatePartialDate(value: unknown): string[] {
  if (!isRecord(value)) return ['Data częściowa musi być obiektem.']
  const errors: string[] = []
  if (Object.keys(value).some((key) => !['year', 'month', 'day'].includes(key))) {
    errors.push('Data częściowa zawiera nieobsługiwane elementy.')
  }
  const year = value.year
  const month = value.month
  const day = value.day
  if (!Number.isInteger(year) || Number(year) < 1000 || Number(year) > 9999) {
    return [...errors, 'Data częściowa wymaga czterocyfrowego roku.']
  }
  if (month !== undefined && (!Number.isInteger(month) || Number(month) < 1 || Number(month) > 12)) {
    errors.push('Miesiąc daty częściowej musi mieścić się w zakresie 1–12.')
  }
  if (day !== undefined && month === undefined) {
    errors.push('Dzień daty częściowej wymaga podania miesiąca.')
  } else if (day !== undefined) {
    const candidate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    if (!Number.isInteger(day) || !validDate(candidate)) errors.push('Dzień nie istnieje w podanym miesiącu i roku.')
  }
  return errors
}

function validateMissing(field: ClinicCrfField, value: MissingValue): string[] {
  const errors: string[] = []
  if (Object.keys(value).length !== 1 || !isRecord(value.__missing)) {
    return ['Brak danych musi mieć kontrolowaną strukturę kodu i powodu.']
  }
  if (Object.keys(value.__missing).some((key) => !['code', 'reason'].includes(key))) {
    errors.push('Brak danych zawiera nieobsługiwane atrybuty.')
  }
  if (!field.missing_codes?.includes(value.__missing.code)) {
    errors.push('Kod braku danych nie jest dozwolony dla tego pola.')
  }
  if (value.__missing.reason !== undefined && typeof value.__missing.reason !== 'string') {
    errors.push('Powód braku danych musi być tekstem.')
  } else if (unicodeLength(value.__missing.reason ?? '') > 1000) {
    errors.push('Powód braku danych może mieć maksymalnie 1000 znaków.')
  }
  if (field.missing_reason_required && !value.__missing.reason?.trim()) {
    errors.push('Ten kod braku danych wymaga udokumentowanego powodu.')
  }
  return errors
}

function validateFieldValue(field: ClinicCrfField, value: unknown): string[] {
  if (isMissingValue(value)) return validateMissing(field, value)
  const errors: string[] = []
  const typeValid = (() => {
    switch (field.type) {
      case 'string':
      case 'text':
      case 'date':
      case 'datetime':
      case 'choice':
        return typeof value === 'string'
      case 'integer':
        return Number.isInteger(value)
      case 'number':
        return typeof value === 'number' && Number.isFinite(value)
      case 'boolean':
        return typeof value === 'boolean'
      case 'multichoice':
        return Array.isArray(value) && value.every((item) => typeof item === 'string')
      case 'partial_date':
        return isRecord(value)
    }
  })()
  if (!typeValid) return ['Nieprawidłowy typ wartości.']

  if (typeof value === 'number') {
    if (field.min !== undefined && value < field.min) errors.push(`Wartość jest mniejsza niż ${field.min}.`)
    if (field.max !== undefined && value > field.max) errors.push(`Wartość jest większa niż ${field.max}.`)
    if (field.type === 'number' && field.precision !== undefined) {
      const scale = 10 ** field.precision
      if (Math.abs(value * scale - Math.round(value * scale)) > 0.00000001) {
        errors.push(`Wartość może mieć maksymalnie ${field.precision} miejsc po przecinku.`)
      }
    }
  }
  if (typeof value === 'string' && ['string', 'text'].includes(field.type)) {
    const max = Math.min(field.max_length ?? (field.type === 'text' ? 100000 : 4000), field.type === 'text' ? 100000 : 4000)
    const length = unicodeLength(value)
    if (field.min_length !== undefined && length < field.min_length) errors.push(`Wartość jest krótsza niż ${field.min_length} znaków.`)
    if (length > max) errors.push(`Wartość jest dłuższa niż ${max} znaków.`)
  }
  if (field.type === 'choice' && typeof value === 'string' && !field.options?.includes(value)) {
    errors.push('Wartość nie należy do kontrolowanego słownika.')
  }
  if (field.type === 'multichoice' && Array.isArray(value) && value.some((entry) => !field.options?.includes(entry))) {
    errors.push('Jedna z wartości nie należy do kontrolowanego słownika.')
  }
  if (field.type === 'date' && typeof value === 'string' && !validDate(value)) errors.push('Data musi mieć format RRRR-MM-DD.')
  if (field.type === 'datetime' && typeof value === 'string' && !validDateTime(value)) errors.push('Data i czas muszą być zgodne z ISO 8601 i zawierać strefę czasową.')
  if (field.type === 'partial_date') errors.push(...validatePartialDate(value))
  return errors
}

function unknownPaths(data: ClinicCrfData, knownPaths: string[], prefix = ''): string[] {
  const unknown: string[] = []
  for (const [key, value] of Object.entries(data)) {
    const path = prefix ? `${prefix}.${key}` : key
    if (knownPaths.includes(path)) continue
    const hasChild = knownPaths.some((known) => known.startsWith(`${path}.`))
    if (hasChild && isRecord(value)) unknown.push(...unknownPaths(value, knownPaths, path))
    else unknown.push(path)
  }
  return unknown
}

export function validateData(definition: ClinicCrfDefinitionV2, data: ClinicCrfData): DataValidationResult {
  const errors: Record<string, string[]> = {}
  const paths = definition.fields.map((field) => field.path)
  unknownPaths(data, paths).forEach((path) => {
    errors[path] = ['Pole nie należy do opublikowanego schematu CRF.']
  })

  const fields: FieldEvaluation[] = definition.fields.map((field) => {
    const present = hasAtPath(data, field.path)
    const value = getAtPath(data, field.path)
    const blank = isBlank(value, present)
    const visible = fieldIsVisible(field, data)
    const required = fieldIsRequired(field, data)
    const fieldErrors: string[] = []
    if (!visible && !blank) fieldErrors.push('Pole nie jest aktywne dla bieżących odpowiedzi i nie może zawierać wartości.')
    else if (visible && required && blank) fieldErrors.push('Pole jest wymagane.')
    else if (visible && !blank) fieldErrors.push(...validateFieldValue(field, value))
    if (fieldErrors.length) errors[field.path] = [...(errors[field.path] ?? []), ...fieldErrors]
    return { field, visible, required, errors: fieldErrors }
  })

  const rules: RuleEvaluation[] = (definition.rules ?? []).map((rule) => {
    const active = !rule.when || evaluateCondition(rule.when, data)
    const passed = !active || evaluateCondition(rule.assert, data)
    if (!passed) errors[rule.path] = [...(errors[rule.path] ?? []), rule.message]
    return { rule, active, passed }
  })
  return { errors, fields, rules, isValid: Object.keys(errors).length === 0 }
}

export function pruneHiddenData(definition: ClinicCrfDefinitionV2, input: ClinicCrfData): ClinicCrfData {
  const data = deepClone(input)
  for (let pass = 0; pass < definition.fields.length + 1; pass += 1) {
    let changed = false
    for (const field of definition.fields) {
      if (!fieldIsVisible(field, data) && hasAtPath(data, field.path)) {
        deleteAtPath(data, field.path)
        changed = true
      }
    }
    if (!changed) break
  }
  return data
}

export function syntheticValueFor(field: ClinicCrfField): unknown {
  if (field.options?.length) return field.type === 'multichoice' ? [field.options[0]] : field.options[0]
  switch (field.type) {
    case 'boolean':
      return true
    case 'integer':
      return Math.round(
        field.min !== undefined && field.max !== undefined
          ? (field.min + field.max) / 2
          : field.min ?? Math.min(field.max ?? 42, 42),
      )
    case 'number':
      {
        const value = field.min !== undefined && field.max !== undefined
          ? (field.min + field.max) / 2
          : field.min ?? Math.min(field.max ?? 1, 1)
        return field.precision === undefined ? value : Number(value.toFixed(field.precision))
      }
    case 'date':
      return '2026-01-15'
    case 'datetime':
      return '2026-01-15T10:00:00Z'
    case 'partial_date':
      return { year: 2026, month: 1 } satisfies PartialDateValue
    case 'text':
      return 'Syntetyczna odpowiedź testowa do walidacji formularza.'
    default:
      return 'Dane syntetyczne'
  }
}

export function generateSyntheticData(definition: ClinicCrfDefinitionV2): ClinicCrfData {
  const data: ClinicCrfData = {}
  for (const field of definition.fields) {
    if (field.source === 'derived' || !fieldIsVisible(field, data)) continue
    setAtPath(data, field.path, syntheticValueFor(field))
  }
  return pruneHiddenData(definition, data)
}

export function emptyDefinition(): ClinicCrfDefinitionV2 {
  return {
    version: CLINIC_CRF_V2,
    fields: [
      {
        path: 'assessment.result',
        label: 'Wynik oceny',
        type: 'choice',
        required: true,
        options: ['yes', 'no'],
        source: 'site',
      },
    ],
    rules: [],
  }
}
